/**
 * The Glass — canon loader (wave glass-1, #790; THE GLASS ruled #786).
 *
 * The pane at /engineering/glass renders CANON, never authored state (the
 * glass constitution, design-786 §2.10). The canon's homes are
 * engineering/contract/ and engineering/docs/; a build step
 * (scripts/generate-glass-canon.mjs) packages the needed pieces into
 * glass-canon.generated.json at build time — generated and gitignored, never
 * committed (registry.json is committed because shared IS its canon's home;
 * this one's homes are elsewhere, and a committed copy would be a second home
 * that drifts).
 *
 * `GlassCanonSchema.parse(canonJson)` runs at module load — the registry.ts
 * posture: a mis-shaped generation throws at import and cannot load dishonest.
 */

import { z } from "zod";
import canonJson from "./glass-canon.generated.json";

const NodeRefSchema = z
  .object({ id: z.string().min(1), text: z.string().min(1) })
  .strict();

/** One arrow of the one-Referral trace, projected mechanically from the
 *  diagram's edge statements in line order (see the generator's header). */
export const GlassTraceStepSchema = z
  .object({
    from: NodeRefSchema,
    to: NodeRefSchema,
    label: z.string().nullable(),
  })
  .strict();

export const GlassProcessSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    does: z.string().min(1),
    terms: z.array(z.string().min(1)).min(1),
    where: z.string().min(1),
    addedBy: z.string().nullable(),
  })
  .strict();

/** One ratified glossary term — a field selection of its glossary.yaml
 *  entry, values verbatim (wave glass-2, #793). */
export const GlassTermSchema = z
  .object({
    term: z.string().min(1),
    aliases: z.array(z.string().min(1)),
    avoid: z.array(z.string().min(1)),
    definition: z.string().min(1),
    spokenAs: z.string().nullable(),
    preferInWriting: z.string().nullable(),
    ratifiedOn: z.string().min(1),
  })
  .strict();

export const GlassTermCategorySchema = z
  .object({
    id: z.string().min(1),
    terms: z.array(GlassTermSchema).min(1),
  })
  .strict();

/** One ruled-out word — the record of the ruling (glossary-ruled-out.yaml),
 *  never a definition; ruledOn is null where the word was ruled by group and
 *  the date lives in dispositionRuling. */
export const GlassRuledOutEntrySchema = z
  .object({
    term: z.string().min(1),
    aliases: z.array(z.string().min(1)),
    disposition: z.enum([
      "retired",
      "use-instead",
      "dictionary-register",
      "ordinary",
    ]),
    useInstead: z.string().nullable(),
    ruledOn: z.string().nullable(),
    dispositionRuling: z.string().min(1),
    // A live thing whose name collides with this word (#904, 2026-09-22) —
    // the entry's see_also, verbatim; null where the entry carries none.
    seeAlso: z.string().nullable(),
  })
  .strict();

/** One retired id — retired.ts's entry, values verbatim. */
export const GlassRetiredEntrySchema = z
  .object({
    id: z.string().min(1),
    kind: z.string().min(1),
    what: z.string().min(1),
    aliases: z.array(z.string().min(1)),
    supersededBy: z.string().nullable(),
    ratifiedOn: z.string().min(1),
    rationale: z.string().min(1),
  })
  .strict();

export const GlassVocabularySchema = z
  .object({
    glossary: z
      .object({
        status: z.string().min(1),
        ratifiedOn: z.string().min(1),
        ratifiedBy: z.string().min(1),
        count: z.number().int().positive(),
        categories: z.array(GlassTermCategorySchema).min(1),
      })
      .strict(),
    ruledOut: z
      .object({
        ruledOn: z.string().min(1),
        ruledBy: z.string().min(1),
        whatThisIs: z.string().min(1),
        whatThisIsNote: z.string().min(1),
        entries: z.array(GlassRuledOutEntrySchema).min(1),
      })
      .strict(),
    retired: z
      .object({
        entries: z.array(GlassRetiredEntrySchema).min(1),
      })
      .strict(),
  })
  .strict();

/** One workflow page of the machine canon (wave glass-4, #800) — the
 *  generated page carried as raw markdown PLUS its declared mechanical
 *  extraction (see the generator's header). state stays an open string: a
 *  lawful roster change (a stamp flip) must not break the load. */
export const GlassWorkflowSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    state: z.string().min(1),
    updated: z.string().min(1),
    sourcePath: z.string().min(1),
    markdown: z.string().min(1),
    description: z.string().nullable(),
    trigger: z.string().min(1),
    airtableReads: z.array(z.string().min(1)),
    airtableWrites: z.array(z.string().min(1)),
    onFailure: z.string().nullable(),
    note: z.string().nullable(),
    mermaid: z.string().min(1),
  })
  .strict();

/** One trigger-map table row — the workflow's what-fires-what line. */
export const GlassTriggerMapRowSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    state: z.string().min(1),
    trigger: z.string().min(1),
    airtableReads: z.array(z.string().min(1)),
    airtableWrites: z.array(z.string().min(1)),
  })
  .strict();

export const GlassMachineSchema = z
  .object({
    triggerMap: z
      .object({
        markdown: z.string().min(1),
        mermaid: z.string().min(1),
        rows: z.array(GlassTriggerMapRowSchema).min(1),
      })
      .strict(),
    workflows: z.array(GlassWorkflowSchema).min(1),
  })
  .strict();

/** One committed drift artifact (wave glass-5, #802) — the YAML parse of
 *  engineering/contract/drift/<date>.yaml VERBATIM AND WHOLE under `data`
 *  (keys untouched — snake_case stays, no translation layer), plus its
 *  sourcePath and filename date. moved entries are the report's own lines —
 *  open strings (report wording may lawfully change); moved: [] is the
 *  record of a no-drift run. */
export const GlassDriftArtifactSchema = z
  .object({
    sourcePath: z.string().min(1),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    data: z
      .object({
        run_at: z.string().min(1),
        airtable: z
          .object({
            live: z
              .object({
                tables: z.number().int().nonnegative(),
                fields: z.number().int().nonnegative(),
              })
              .strict(),
            snapshot: z
              .object({
                tables: z.number().int().nonnegative(),
                fields: z.number().int().nonnegative(),
              })
              .strict(),
          })
          .strict(),
        n8n: z
          .object({
            live_nonarchived: z.number().int().nonnegative(),
            roster: z.number().int().nonnegative(),
          })
          .strict(),
        // The cloudflare leg's counts (wave glass-8, #809). OPTIONAL:
        // committed artifacts emitted before the leg existed lack the key
        // and must keep loading; artifacts from the three-legged report
        // carry it.
        cloudflare: z
          .object({
            live: z.number().int().nonnegative(),
            snapshot: z.number().int().nonnegative(),
          })
          .strict()
          .optional(),
        // The github leg's counts (#906, 2026-09-11): live required contexts,
        // the set the roster derives, and enforce_admins as read. OPTIONAL for
        // the same reason as cloudflare — artifacts emitted before leg 4
        // existed lack the key.
        github: z
          .object({
            live: z.number().int().nonnegative(),
            derived: z.number().int().nonnegative(),
            enforce_admins: z.boolean(),
          })
          .strict()
          .optional(),
        // The mailgun leg's counts (#1113, 2026-10-09): live inbound routes
        // and the routes in mailgun-routes.snapshot.json. OPTIONAL for the
        // same reason as cloudflare — artifacts emitted before leg 5 existed
        // lack the key.
        mailgun: z
          .object({
            live: z.number().int().nonnegative(),
            snapshot: z.number().int().nonnegative(),
          })
          .strict()
          .optional(),
        moved: z.array(z.string().min(1)),
      })
      .strict(),
  })
  .strict();

export const GlassDriftSchema = z
  .object({
    artifacts: z.array(GlassDriftArtifactSchema).min(1),
  })
  .strict();

/** One Catalog card — catalog.generated.json's card VERBATIM AND WHOLE (wave
 *  glass-6, #805): every field as the file writes it, keys untouched
 *  (snake_case stays — no translation layer). kind and stamp stay open
 *  strings: a lawful catalog change (a new kind, a new stamp word) must not
 *  break the load. The optional keys are the measured union across the 423
 *  cards at wave open; strict, so a NEW key the catalog generator starts
 *  writing throws at build — a carriage decision to re-rule, never silently
 *  dropped. */
export const GlassCatalogCardSchema = z
  .object({
    id: z.string().min(1),
    kind: z.string().min(1),
    name: z.string().min(1),
    aliases: z.array(z.string()),
    stamp: z.string().min(1),
    text: z.string(),
    source: z.string().min(1),
    ref: z.string().min(1).optional(),
    files: z.array(z.string().min(1)).optional(),
    class: z.string().min(1).optional(),
    type: z.string().min(1).optional(),
    grain: z.string().min(1).optional(),
    avoid: z.array(z.string().min(1)).optional(),
    flags: z.array(z.unknown()).optional(),
    keys: z.array(z.unknown()).optional(),
    dimensions: z.array(z.unknown()).optional(),
    option_meanings: z.unknown().optional(),
    warrant: z.string().min(1).nullable().optional(),
    status_evidence: z.string().min(1).optional(),
  })
  .strict();

/** One typed Catalog edge — the F56 ruled edge names stay as stored. */
export const GlassCatalogEdgeSchema = z
  .object({
    from: z.string().min(1),
    to: z.string().min(1),
    type: z.string().min(1),
    cite: z.string().min(1).optional(),
    note: z.string().min(1).optional(),
  })
  .strict();

/** The committed Catalog (engineering/contract/catalog.generated.json)
 *  VERBATIM AND WHOLE — already generated (bin/generate-catalog.py) and
 *  CI-compared (contract-gates.yml); the generator re-checks the file's own
 *  counts and edge-endpoint coherence at build. displayed_on and lineage are
 *  carried as the file writes them (open shapes — their structure is the
 *  catalog generator's, not this loader's, to rule). */
export const GlassCatalogSchema = z
  .object({
    version: z.number().int(),
    issue: z.unknown(),
    what_this_is: z.string().min(1),
    displayed_on: z
      .object({ status: z.string().min(1), ruling: z.string().min(1) })
      .strict(),
    generated_from: z.array(z.string().min(1)).min(1),
    lineage: z.unknown(),
    counts: z
      .object({
        cards: z.number().int().positive(),
        edges: z.number().int().nonnegative(),
      })
      .strict(),
    cards: z.array(GlassCatalogCardSchema).min(1),
    edges: z.array(GlassCatalogEdgeSchema),
  })
  .strict();

/** One register row — verified.yaml's row VERBATIM AND WHOLE (wave glass-3,
 *  #795): every field as the file writes it, keys untouched (snake_case
 *  stays — no translation layer). kind and state are the register's ruled
 *  value sets; verdict stays an open string so a lawful third verdict
 *  (COULD NOT VERIFY) cannot break the load. */
export const GlassRegisterRowSchema = z
  .object({
    path: z.string().min(1),
    kind: z.enum(["frozen", "live-claiming", "code"]),
    state: z.enum(["clean", "swept"]),
    verdict: z.string().min(1),
    check: z.string().min(1),
    checked_on: z.string().min(1),
    checker: z.string().min(1),
    rows_at: z.string().min(1),
    failing_claim: z.string().min(1).optional(),
    demoted: z.string().min(1).optional(),
    repair_chain: z.string().min(1).optional(),
  })
  .strict();

/** One activeness surface entry — activeness.yaml's entry VERBATIM AND WHOLE
 *  (wave glass-7, #807): every field as the file writes it, keys untouched
 *  (snake_case stays — no translation layer). kind and stamp stay open
 *  strings: Chris maintains the roster, and a lawful new stamp word must not
 *  break the load. The optional keys are the measured union across the 27
 *  entries at wave open; strict, so a NEW key the file gains throws at
 *  build — a carriage decision to re-rule, never silently dropped. */
export const GlassActivenessWorkflowSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    stamp: z.string().min(1),
  })
  .strict();

/** One duty of a cron-roster entry (#904, 2026-09-22) — what a worker's
 *  handleScheduled calls, on which cron string; verbatim, snake_case kept.
 *  bin/cron-duties-check.py measures the list against the dispatch file. */
export const GlassCronDutySchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    aliases: z.array(z.string().min(1)).optional(),
    handler: z.string().min(1),
    cron: z.string().min(1),
    switch: z.string().min(1).optional(),
    stamp: z.string().min(1),
    does: z.string().min(1),
  })
  .strict();

/** One entry of a communications-roster (#964, 2026-09-24) — an email or page LegalFlare sends
 *  to a Firm's Contact or a Potential Client; verbatim, snake_case kept. Its examples are
 *  committed under engineering/docs/communications/; bin/communications-check.py measures its
 *  fingerprint against the composing code. */
export const GlassCommunicationFingerprintSchema = z.union([
  z.object({ file: z.string().min(1), roots: z.array(z.string().min(1)).min(1), sha256: z.string().min(1) }).strict(),
  z.object({ n8n: z.string().min(1), version: z.string().min(1) }).strict(),
  z.object({ airtable: z.string().min(1), fields: z.array(z.string().min(1)).min(1), sha256: z.string().min(1) }).strict(),
]);

export const GlassCommunicationSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    aliases: z.array(z.string().min(1)).optional(),
    audience: z.string().min(1),
    glossary_kind: z.string().min(1),
    process: z.string().min(1),
    channel: z.string().min(1),
    sender: z.string().min(1),
    subject: z.string().min(1),
    trigger: z.string().min(1),
    opt_in: z.string().min(1),
    says: z.string().min(1),
    recipient_can: z.array(z.string().min(1)).min(1),
    stamp: z.string().min(1),
    note: z.string().min(1).optional(),
    examples: z.array(z.string().min(1)).min(1),
    fingerprint: z.array(GlassCommunicationFingerprintSchema).min(1),
  })
  .strict();

/** One channel of an external surface's channel roster (#1050, 2026-10-09) — the `slack` entry's
 *  channels: the name Slack shows, its Slack id, what it is for and which system posts to it;
 *  verbatim, snake_case kept. bin/slack-channels-check.py measures the list against the portal
 *  worker's SLACK_CHANNEL_IDS and the channel ids in the n8n exports. */
export const GlassSlackChannelSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    slack_id: z.string().min(1),
    purpose: z.string().min(1),
    posted_by: z.array(z.string().min(1)).min(1),
    stamp: z.string().min(1),
    note: z.string().min(1).optional(),
  })
  .strict();

export const GlassActivenessSurfaceSchema = z
  .object({
    id: z.string().min(1),
    kind: z.string().min(1),
    ref: z.string().min(1),
    stamp: z.string().min(1),
    note: z.string().min(1),
    warrant: z.string().min(1).optional(),
    presence_exempt: z.string().min(1).optional(),
    verified: z.string().min(1).optional(),
    workflows: z.array(GlassActivenessWorkflowSchema).min(1).optional(),
    // A cron-roster's two measured files and its duties (#904, 2026-09-22).
    dispatch: z.string().min(1).optional(),
    triggers: z.string().min(1).optional(),
    duties: z.array(GlassCronDutySchema).min(1).optional(),
    // A communications-roster's entries (#964, 2026-09-24).
    communications: z.array(GlassCommunicationSchema).min(1).optional(),
    // An external surface's channels — the slack entry (#1050, 2026-10-09).
    channels: z.array(GlassSlackChannelSchema).min(1).optional(),
    route: z
      .object({ answers: z.string().min(1), caveats: z.string().min(1) })
      .strict()
      .optional(),
    // The actor's CI gate (#906, ruled 2026-09-11): the workflow file, the
    // required status context (its job key), and the input globs the gate
    // diffs on every pull request. Present only on live actors with a gate.
    ci_gate: z
      .object({
        workflow: z.string().min(1),
        context: z.string().min(1),
        inputs: z.array(z.string().min(1)).min(1),
      })
      .strict()
      .optional(),
  })
  .strict();

/** One ratification mark — the generator's DECLARED MECHANICAL DERIVATION
 *  (see its header): phrase PRESENCE in the entry's warrant +
 *  presence_exempt text, never a judgment of what the phrase means today.
 *  pendingPhrase is the matched phrase verbatim, or null. */
export const GlassRatificationMarkSchema = z
  .object({
    id: z.string().min(1),
    hasRatified: z.boolean(),
    pendingPhrase: z.string().min(1).nullable(),
  })
  .strict();

/** One entrance step — entrance.yaml's step VERBATIM AND WHOLE, keys
 *  untouched. */
export const GlassEntranceStepSchema = z
  .object({
    order: z.number().int().positive(),
    name: z.string().min(1),
    instruction: z.string().min(1),
    points_at: z.array(z.string().min(1)),
    bound: z.string().min(1).optional(),
    guardrail: z
      .object({ law: z.string().min(1), specimen: z.string().min(1) })
      .strict(),
  })
  .strict();

/** The stewardship canon (wave glass-7, #807): activeness.yaml and
 *  entrance.yaml verbatim and whole, plus the declared ratification marks.
 *  entrance status stays an open string — Chris flips draft → ratified by
 *  ruling (the file's own header), and that lawful flip must not break the
 *  load. */
export const GlassStewardshipSchema = z
  .object({
    activeness: z
      .object({
        sourcePath: z.string().min(1),
        data: z
          .object({
            version: z.number().int(),
            maintained_by: z.string().min(1),
            seeded: z.string().min(1),
            thread: z.number().int(),
            surfaces: z.array(GlassActivenessSurfaceSchema).min(1),
          })
          .strict(),
        ratificationMarks: z.array(GlassRatificationMarkSchema).min(1),
      })
      .strict(),
    entrance: z
      .object({
        sourcePath: z.string().min(1),
        data: z
          .object({
            status: z.string().min(1),
            scope: z.string().min(1),
            preamble: z.string().min(1),
            steps: z.array(GlassEntranceStepSchema).min(1),
          })
          .strict(),
      })
      .strict(),
  })
  .strict();

export const GlassCanonSchema = z
  .object({
    meta: z
      .object({
        generatedBy: z.string().startsWith("GENERATED:"),
        generatedAt: z.string().min(1),
        sourceCommit: z.string().nullable(),
        sources: z
          .object({
            context: z.string(),
            overview: z.string(),
            busMatrix: z.string(),
            register: z.string(),
            glossary: z.string(),
            ruledOut: z.string(),
            retired: z.string(),
            triggerMap: z.string(),
            workflows: z.string(),
            drift: z.string(),
            catalog: z.string(),
            activeness: z.string(),
            entrance: z.string(),
          })
          .strict(),
      })
      .strict(),
    context: z
      .object({
        mermaid: z.string().min(1),
        readingNotes: z.string().min(1),
      })
      .strict(),
    busMatrix: z
      .object({
        version: z.number().int(),
        drafted: z.string().min(1),
        ratified: z.string().min(1),
        // The day a row was last added after ratification (#904); null until one was.
        amended: z.string().min(1).nullable(),
        processes: z.array(GlassProcessSchema).min(1),
      })
      .strict(),
    overview: z
      .object({
        chassisMermaid: z.string().min(1),
        traceMermaid: z.string().min(1),
        traceSteps: z.array(GlassTraceStepSchema).min(1),
        traceProjection: z
          .object({
            arrowLines: z.number().int().nonnegative(),
            matched: z.number().int().nonnegative(),
          })
          .strict(),
      })
      .strict(),
    vocabulary: GlassVocabularySchema,
    machine: GlassMachineSchema,
    drift: GlassDriftSchema,
    catalog: GlassCatalogSchema,
    stewardship: GlassStewardshipSchema,
    register: z
      .object({
        rowCount: z.number().int().positive(),
        tally: z
          .object({
            files: z.number().int().nonnegative(),
            clean: z.number().int().nonnegative(),
            swept: z.number().int().nonnegative(),
          })
          .strict(),
        generatedOn: z.string().min(1),
        oldestCheckedOn: z.string().min(1),
        newestCheckedOn: z.string().min(1),
        rows: z.array(GlassRegisterRowSchema).min(1),
      })
      .strict(),
  })
  .strict();

export type GlassCanon = z.infer<typeof GlassCanonSchema>;
export type GlassCatalog = z.infer<typeof GlassCatalogSchema>;
export type GlassCatalogCard = z.infer<typeof GlassCatalogCardSchema>;
export type GlassCatalogEdge = z.infer<typeof GlassCatalogEdgeSchema>;
export type GlassDrift = z.infer<typeof GlassDriftSchema>;
export type GlassDriftArtifact = z.infer<typeof GlassDriftArtifactSchema>;
export type GlassMachine = z.infer<typeof GlassMachineSchema>;
export type GlassWorkflow = z.infer<typeof GlassWorkflowSchema>;
export type GlassTriggerMapRow = z.infer<typeof GlassTriggerMapRowSchema>;
export type GlassRegisterRow = z.infer<typeof GlassRegisterRowSchema>;
export type GlassProcess = z.infer<typeof GlassProcessSchema>;
export type GlassTraceStep = z.infer<typeof GlassTraceStepSchema>;
export type GlassTerm = z.infer<typeof GlassTermSchema>;
export type GlassTermCategory = z.infer<typeof GlassTermCategorySchema>;
export type GlassRuledOutEntry = z.infer<typeof GlassRuledOutEntrySchema>;
export type GlassRetiredEntry = z.infer<typeof GlassRetiredEntrySchema>;
export type GlassVocabulary = z.infer<typeof GlassVocabularySchema>;
export type GlassStewardship = z.infer<typeof GlassStewardshipSchema>;
export type GlassActivenessSurface = z.infer<typeof GlassActivenessSurfaceSchema>;
export type GlassCommunication = z.infer<typeof GlassCommunicationSchema>;
export type GlassRatificationMark = z.infer<typeof GlassRatificationMarkSchema>;
export type GlassEntranceStep = z.infer<typeof GlassEntranceStepSchema>;

/** The packaged canon — validated at module load; throws on a bad generation. */
export const GLASS_CANON: GlassCanon = GlassCanonSchema.parse(canonJson);
